import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { supabase } from '@/lib/supabase';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { AlertCircle, Loader2, UserPlus } from 'lucide-react';
import SEO from '../Components/SEO.jsx';

export default function Register() {
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        azienda: '',
        telefono: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (formData.password.length < 8) {
            setError('La password deve essere di almeno 8 caratteri.');
            setLoading(false);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Le password non corrispondono.');
            setLoading(false);
            return;
        }

        try {
            const { error: signUpError } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        full_name: formData.full_name,
                        company: formData.azienda,
                        phone: formData.telefono,
                        role: 'user' // Default role
                    },
                    emailRedirectTo: `${window.location.origin}/email-verified`
                }
            });

            if (signUpError) throw signUpError;

            // Se la registrazione ha successo, mostriamo il messaggio di conferma
            setSuccess(true);
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.message || 'Errore durante la registrazione. Riprova.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 pt-20">
            <SEO
                title="Registrazione - Crea Account | Tecnosys"
                description="Registrati per accedere all'area clienti Tecnosys"
            />

            <Card className="w-full max-w-md">
                <CardHeader className="text-center space-y-2">
                    <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                        <UserPlus className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-2xl">Crea Account Tecnosys</CardTitle>
                    <p className="text-gray-600 text-sm">Compila i dati per registrarti</p>
                </CardHeader>
                <CardContent>
                    {success ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                                <UserPlus className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Registrazione completata!</h3>
                            <p className="text-gray-600 mb-6">Riceverai un'email per attivare il tuo account.</p>
                            <Link to={createPageUrl('Login')}>
                                <Button className="rounded-full bg-blue-600 hover:bg-blue-700 text-white">
                                    Vai al Login
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div>
                                <Label htmlFor="full_name">Nome e Cognome *</Label>
                                <Input
                                    id="full_name"
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                    required
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="password">Password (min 8 caratteri) *</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    minLength={8}
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="confirmPassword">Conferma Password *</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    required
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="azienda">Azienda</Label>
                                <Input
                                    id="azienda"
                                    value={formData.azienda}
                                    onChange={(e) => setFormData({ ...formData, azienda: e.target.value })}
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label htmlFor="telefono">Telefono</Label>
                                <Input
                                    id="telefono"
                                    type="tel"
                                    value={formData.telefono}
                                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                    className="mt-1"
                                />
                            </div>

                            {error && (
                                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                                    <AlertCircle className="w-4 h-4" />
                                    {error}
                                </div>
                            )}

                            <Button type="submit" className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Registrazione...
                                    </>
                                ) : (
                                    'Registrati'
                                )}
                            </Button>
                        </form>
                    )}

                    {!success && (
                        <div className="mt-6 text-center space-y-3">
                            <p className="text-sm text-gray-600">
                                Hai già un account?{' '}
                                <Link to={createPageUrl('Login')} className="text-blue-600 hover:underline font-medium">
                                    Accedi
                                </Link>
                            </p>
                            <Link to={createPageUrl('Home')} className="block text-sm text-gray-500 hover:text-gray-700">
                                ← Torna alla home
                            </Link>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}